import { DialogForm, Label, useToast } from '@ais-ui/ui-core';
import React, { useState } from 'react';
import { useDeleteTrack } from '@ais-ui/api';
import { mutateCallbacks } from '../../utils/mutateCallbacks';
import type { TrackResult } from '@ais-ui/api-spec/portserver';
import { useLocalization } from '@ais-ui/locale-management';

interface DeleteRouteProps {
  route: TrackResult;
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
}

export function DeleteRoute({
  route,
  showDialog,
  setShowDialog,
}: DeleteRouteProps) {
  const { messages } = useLocalization();
  const { mutate } = useDeleteTrack();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { toast } = useToast();

  const onDelete = () => {
    setIsSubmitting(true);

    const callBacks = mutateCallbacks(
      toast,
      messages,
      'delete',
      'route',
      setIsSubmitting,
      setShowDialog,
    );

    if (!route.id) {
      toast({
        className: 'delete-route-error-toast',
        title: messages.genericToastTitleError,
        description: messages.routesDeleteErrorMessage,
        variant: 'destructive',
      });
      setShowDialog(false);
      setIsSubmitting(false);
      return;
    }

    mutate(route.id, callBacks);
  };

  return (
    <DialogForm showDialog={showDialog} onOpenChange={setShowDialog} size="sm">
      <DialogForm.Header>
        <DialogForm.Title>{messages.routesDeleteTitle}</DialogForm.Title>
        <DialogForm.Description>
          {messages.routesDeleteWarning}
        </DialogForm.Description>
      </DialogForm.Header>
      <DialogForm.Body>
        <div className="grid grid-cols-[auto_minmax(50%,_1fr)] 4 gap-x-2 items-baseline">
          <Label className="font-light">{messages.routesDeleteRouteName}</Label>
          <Label htmlFor="route-name">{route?.source?.ship?.name}</Label>
        </div>
      </DialogForm.Body>
      <DialogForm.Footer>
        <DialogForm.Cancel disabled={isSubmitting}>
          {messages.genericCancel}
        </DialogForm.Cancel>
        <DialogForm.Submit
          variant="destructive"
          id="route-confirm-delete-button"
          disabled={isSubmitting}
          onSubmit={onDelete}
        >
          {isSubmitting ? messages.genericDeleting : messages.genericDelete}
        </DialogForm.Submit>
      </DialogForm.Footer>
    </DialogForm>
  );
}
